import React, { createContext, useContext, useMemo } from 'react';
import { Checkbox, Divider, dividerClasses, FormControlLabel, Paper, SxProps, Theme } from '@mui/material';
import { Grid, Stack } from '@mui/system';

import { PropertySelectField } from '@paragon/client-tools';
import { DownloadMachineryTrsMemberType, DownloadRequest } from '@paragon/contract-library';
import {
  canStartTrial,
  getLoggedInUser,
  hasAnalysisEvaluationPermission as hasAnalysisEvaluationPermissionSelector,
  hasBladeDownloadsPermission as hasBladeDownloadsPermissionSelector,
  hasCanUsePlatesPermission as hasCanUsePlatesPermissionSelector,
  hasComparePlatePricesPermission,
  hasCybDownloadsPermission as hasCybDownloadsPermissionSelector,
  hasFreeSubscription as hasFreeSubscriptionSelector,
  hasNewLayoutModeFeatureFlag as hasNewLayoutModeFeatureFlagSelector,
  hasSealsAndSignaturesPermission as hasSealsAndSignaturesPermissionSelector,
  hasSteelTrsProfilePermission as hasSteelTrsProfilePermissionSelector,
  hasTrussDesignDownloadsPermission as hasTrussDesignDownloadsPermissionSelector,
  hasTrussDesignPermission as hasTrussDesignPermissionSelector,
  isLoggedInUserRoot,
} from '@paragon/login-tools';
import { DistanceField } from '@paragon/material';
import { getPlateManufacturer } from '@paragon/material-lists';
import { enqueueError } from '@paragon/notification-tools';
import { assertExhaustiveSwitch } from '@paragon/utilities';

import { getAppMonitor } from '../../../monitor';
import {
  downloadAnalysisReportPdf,
  downloadCanaryCsv,
  downloadConvertedFileType,
  downloadLayoutPdf,
  downloadLoadCaseCsv,
  downloadMachineryTrs,
  downloadReport,
  downloadShopDrawings,
  downloadStlForActiveComponentDesign,
  downloadTrussDesignDrawing,
  exportActiveLayoutToIfcFile,
  generateDigitalQcFileForActiveComponentDesign,
  generateDxfForActiveComponentDesign,
} from '../../../store/actions/downloadActions';
import { downloadLayoutAsDxf } from '../../../store/actions/layout/layoutActions';
import { startCanaryRecordingIfHasPermissions } from '../../../store/actions/paragonProcessActions';
import {
  showQualityControlDigitalQcDownloadDialog,
  showQualityControlPdfDownloadDialog,
  showSelectPlateListsDialog,
} from '../../../store/actions/popupActions';
import { AsyncThunkActionVoid, ThunkActionVoid } from '../../../store/actions/thunkActions';
import { getAnalysisSetGuidToComponentDesignGuid } from '../../../store/selectors/analysisSetsSelectors';
import {
  getDownloadRequestForBulk,
  getDownloadRequestForSingle,
  getDownloadRequestGuidTypeForContext,
  getRequestForAnalysisSetBeingViewed,
} from '../../../store/selectors/downloadSelectors';
import { getActiveAnalysisSet } from '../../../store/selectors/getActiveAnalysisSetSelector';
import { getActiveComponentDesign } from '../../../store/selectors/getActiveComponentDesignSelector';
import {
  getCommittedComponentDesigns,
  getCommittedComponentDesignsByGuid,
} from '../../../store/selectors/getComponentDesignsSelectors';
import { getProjectLayoutModel } from '../../../store/selectors/getProjectLayoutModel';
import { getSingleSelectedItemEntryOrNull } from '../../../store/selectors/getSelectedItemSelector';
import { getAllBuildings, getAllLevels } from '../../../store/selectors/layoutDataSelectors';
import { anyLayoutItemsExist } from '../../../store/selectors/layoutModeSelectors';
import { endCanaryRecording } from '../../../store/slices/canaryDownloadSlice';
import { useAppDispatch, useAppSelector } from '../../hooks';
import { DownloadListItem } from './DownloadListItem';
import { DownloadMode } from './DownloadsDialog';
import {
  TrussDesignDrawingDownloadListItem,
  useTrussDesignDrawingDownloadChoices,
} from './TrussDesignDrawingDownloadListItem';

const context = createContext({
  mode: 'bulk' as DownloadMode,
  onBeforeDownloadBegins: (() => {}) as (() => void) | undefined,
});

export function useDownloadContext() {
  return useContext(context);
}

export interface DownloadListItemsProps {
  readonly sx?: SxProps<Theme>;

  readonly mode: DownloadMode;
  readonly customQuantities?: { [guid: string]: number };
  readonly onBeforeDownloadBegins?: () => void;

  readonly showAppSpinner: boolean;
}

export function DownloadListItems({
  sx,
  mode,
  customQuantities,
  onBeforeDownloadBegins,
  showAppSpinner,
}: DownloadListItemsProps) {
  const dispatch = useAppDispatch();

  const activeAnalysisSet = useAppSelector(getActiveAnalysisSet);
  const isRootUser = useAppSelector(isLoggedInUserRoot);
  const hasTrussDesignPermission = useAppSelector(hasTrussDesignPermissionSelector);
  const hasNewLayoutModeFeatureFlag = useAppSelector(hasNewLayoutModeFeatureFlagSelector);
  const hasTrussDesignDownloadsPermission = useAppSelector(hasTrussDesignDownloadsPermissionSelector);
  const hasSealsAndSignaturesPermission = useAppSelector(hasSealsAndSignaturesPermissionSelector);
  const hasAnalysisEvaluationPermission = useAppSelector(hasAnalysisEvaluationPermissionSelector);
  const selectedEntry = useAppSelector(getSingleSelectedItemEntryOrNull);
  const isMemberSelected = selectedEntry && (selectedEntry.type === 'member' || selectedEntry.type === 'brace');
  const isRecordingCanaries = useAppSelector((state) => state.canaryDownload.recording);
  const layoutPresent = useAppSelector(anyLayoutItemsExist);
  const isFreeUser = useAppSelector(hasFreeSubscriptionSelector);
  const trialAvailable = useAppSelector(canStartTrial);
  const activeComponentDesign = useAppSelector(getActiveComponentDesign);
  const committedComponentDesignsByGuid = useAppSelector(getCommittedComponentDesignsByGuid);
  const committedComponentDesigns = useAppSelector(getCommittedComponentDesigns);
  const sharedComponentSet = useAppSelector((state) => state.sharedComponentSet.sharedComponentSet);
  const loggedInUser = useAppSelector(getLoggedInUser);
  const hasBladeFeatureFlag = useAppSelector(hasBladeDownloadsPermissionSelector);
  const hasCybFeatureFlag = useAppSelector(hasCybDownloadsPermissionSelector);
  const hasSteelTrsFeatureFlag = useAppSelector(hasSteelTrsProfilePermissionSelector);
  const canUsePlates = useAppSelector(hasCanUsePlatesPermissionSelector);
  const downloadRequestGuidTypeForContext = useAppSelector(getDownloadRequestGuidTypeForContext);
  const downloadRequestForBulk = useAppSelector(getDownloadRequestForBulk);
  const downloadRequestForSingle = useAppSelector(getDownloadRequestForSingle);
  const analysisSetGuidToComponentDesignGuid = useAppSelector(getAnalysisSetGuidToComponentDesignGuid);
  const requestForAnalysisSetBeingViewed = useAppSelector(getRequestForAnalysisSetBeingViewed);
  const canComparePlatePrices = useAppSelector(hasComparePlatePricesPermission) || isRootUser;
  const levels = useAppSelector(getAllLevels);
  const levelGuids = useMemo(() => levels.map(({ guid }) => guid), [levels]);
  const buildings = useAppSelector(getAllBuildings);
  const hasMultipleBuildings = buildings.length > 1;
  const insertions = useAppSelector((state) => state.insertions);
  const projectLayoutModel = useAppSelector(getProjectLayoutModel);
  const projectName = projectLayoutModel?.name;
  const isInProjectContext = useAppSelector((state) => state.application.openContext?.type === 'project');
  const { templates: trussDesignDrawingTemplates, canIgnoreErrors: canIgnoreTddErrors } =
    useTrussDesignDrawingDownloadChoices();
  const canShareProjectSnapshot = !!loggedInUser && isInProjectContext && !!projectLayoutModel;

  const [selectedBuildingGuid, setSelectedBuildingGuid] = React.useState<string | 'all'>('all');
  const [selectedLevelGuid, setSelectedLevelGuid] = React.useState<string | 'all'>('all');
  const [trsMemberType, setTrsMemberType] = React.useState<DownloadMachineryTrsMemberType>('All');
  const [useMachineryTrsMaxMemberLength, setUseMachineryTrsMaxMemberLength] = React.useState<boolean>(false);
  const [machineryTrsMaxMemberLengthInInches, setMachineryTrsMaxMemberLengthInInches] = React.useState<number>(3 * 12);
  const [
    includeMembersLongerThanMaxLengthInSeparateMachineryTrsFile,
    setIncludeMembersLongerThanMaxLengthInSeparateMachineryTrsFile,
  ] = React.useState<boolean | null>(null);

  const levelGuidsForBuildingGuid = (buildingGuid: string) =>
    insertions
      .filter(({ objectGuid, parentGuid }) => parentGuid === buildingGuid && levelGuids.includes(objectGuid))
      .map(({ objectGuid }) => objectGuid);
  const getBuildingOptionName = (option: string | 'all') =>
    option === 'all' ? 'All Buildings' : (buildings.find(({ guid }) => guid === option)?.name.trim() ?? option);
  const getLevelOptionName = (option: string | 'all') =>
    option === 'all' ? 'All Levels' : (levels.find(({ guid }) => guid === option)?.name.trim() ?? option);
  const selectedLevelName = `(${getBuildingOptionName(selectedBuildingGuid)}) ${getLevelOptionName(selectedLevelGuid)}`;
  const selectedLevelFullName = `${projectName ? `${projectName.trim()} ` : ''}${selectedLevelName}`;

  let buildingSelect: React.ReactNode = null;
  let layoutLevelSelect: React.ReactNode = null;

  if (hasMultipleBuildings) {
    // show building picker, level picker if needed
    const levelsForBuilding = levelGuidsForBuildingGuid(selectedBuildingGuid);
    buildingSelect = (
      <PropertySelectField<string>
        property="Building"
        options={['all', ...buildings.map((b) => b.guid)]}
        getName={getBuildingOptionName}
        value={selectedBuildingGuid}
        submit={(value) => {
          setSelectedBuildingGuid(value);

          // So it doesn't say "All Levels"
          setSelectedLevelGuid(value === 'all' ? 'all' : (levelGuidsForBuildingGuid(value)[0] ?? 'all'));
        }}
      />
    );
    if (levelsForBuilding.length > 1) {
      layoutLevelSelect = (
        <PropertySelectField<string>
          property="Level"
          options={levelsForBuilding}
          getName={getLevelOptionName}
          value={selectedLevelGuid}
          submit={setSelectedLevelGuid}
        />
      );
    }
  } else if (levelGuids.length > 1) {
    // show level picker only
    layoutLevelSelect = (
      <PropertySelectField<string>
        property="Level"
        options={['all', ...levelGuids]}
        getName={getLevelOptionName}
        value={selectedLevelGuid}
        submit={(value) => {
          setSelectedLevelGuid(value);

          // So the downloaded file doesn't say "All Buildings"
          setSelectedBuildingGuid(buildings[0].guid);
        }}
      />
    );
  }
  // show no pickers

  function createBuildDownloadRequest(
    callbackDownloadAction: (downloadRequest: DownloadRequest) => Promise<void>,
    description: string,
  ) {
    return async () => {
      let downloadRequest: DownloadRequest;
      switch (mode) {
        case 'bulk':
          if (!downloadRequestForBulk) {
            dispatch(enqueueError('Download failed. You may have to wait for the context to finish loading.'));
            return;
          }
          downloadRequest = downloadRequestForBulk;
          break;

        case 'custom':
          if (!customQuantities) {
            throw new Error('Custom quantities must be provided for custom download mode');
          }
          if (!downloadRequestForBulk) {
            dispatch(enqueueError('Download failed. You may have to wait for the context to finish loading.'));
            return;
          }
          downloadRequest = { ...downloadRequestForBulk, guidToQuantity: customQuantities };
          break;

        case 'single':
          if (!downloadRequestForSingle) {
            dispatch(enqueueError('Download failed. You may have to wait for the context to finish loading.'));
            return;
          }
          downloadRequest = downloadRequestForSingle;
          break;

        default:
          assertExhaustiveSwitch(mode);
      }

      await callbackDownloadAction(downloadRequest);

      recordInteraction(description);
    };
  }

  function createDispatchDownloadWithUniqueRequest(downloadAction: () => AsyncThunkActionVoid, description: string) {
    return async () => {
      await dispatch(downloadAction());

      recordInteraction(description);
    };
  }

  function createNonAsyncDispatchDownloadWithUniqueRequest(downloadAction: () => ThunkActionVoid, description: string) {
    return async () => {
      dispatch(downloadAction());

      recordInteraction(description);
    };
  }

  const contextValue = useMemo(() => ({ mode, onBeforeDownloadBegins }), [mode, onBeforeDownloadBegins]);

  function recordInteraction(message: string) {
    const monitor = getAppMonitor();
    const metadata = {
      mode,
      usingSharedComponentSet: !!sharedComponentSet,
    };

    monitor.recordInteraction(message, metadata);
  }

  const onSeparateShopDrawingDownloadClick = createBuildDownloadRequest(
    (downloadRequest) => dispatch(downloadShopDrawings(downloadRequest, mode, false)),
    'Shop Drawings downloaded',
  );

  let platePickListPdfDownloadDisabled: boolean;
  switch (mode) {
    case 'bulk':
      platePickListPdfDownloadDisabled =
        committedComponentDesigns.length === 0 ||
        committedComponentDesigns.every((componentDesign) => componentDesign.component.platePairs.length === 0);
      break;
    case 'custom':
      if (!customQuantities) {
        throw new Error('Custom quantities must be provided for custom download mode');
      }
      platePickListPdfDownloadDisabled = Object.entries(customQuantities)
        .filter(([_, quantity]) => quantity !== 0)
        .every(
          ([guid]) =>
            committedComponentDesignsByGuid[
              downloadRequestGuidTypeForContext === 'AnalysisSet' ? analysisSetGuidToComponentDesignGuid[guid] : guid
            ]?.component.platePairs.length === 0,
        );
      break;
    case 'single':
      platePickListPdfDownloadDisabled =
        !activeComponentDesign || activeComponentDesign.component.platePairs.length === 0;
      break;

    default:
      assertExhaustiveSwitch(mode);
  }

  return (
    <context.Provider value={contextValue}>
      <Paper
        sx={[
          ...(Array.isArray(sx) ? sx : [sx]),

          {
            [`& .${dividerClasses.root}:first-child`]: { display: 'none' },
            [`& .${dividerClasses.root}:last-child`]: { display: 'none' },
            [`& .${dividerClasses.root} + .${dividerClasses.root}`]: { display: 'none' },
          },
        ]}
      >
        {/* section: models and drawings */}

        <DownloadListItem
          for={['single-component']}
          description="Component DXF"
          visible={hasTrussDesignDownloadsPermission && hasSealsAndSignaturesPermission}
          onClick={createDispatchDownloadWithUniqueRequest(
            generateDxfForActiveComponentDesign,
            'DXF for active component design downloaded',
          )}
        />

        <DownloadListItem
          for={['whole-layout']}
          description="Layout DXF"
          visible={layoutPresent}
          disabled={layoutPresent ? false : undefined}
          onClick={createDispatchDownloadWithUniqueRequest(
            selectedLevelGuid === 'all'
              ? downloadLayoutAsDxf()
              : downloadLayoutAsDxf(selectedLevelGuid, selectedLevelFullName),
            'Layout DXF file downloaded',
          )}
        >
          {(buildingSelect || layoutLevelSelect) && (
            <Grid container spacing={1}>
              {buildingSelect && <Grid size={layoutLevelSelect ? 6 : 12}>{buildingSelect}</Grid>}
              {layoutLevelSelect && <Grid size={buildingSelect ? 6 : 12}>{layoutLevelSelect}</Grid>}
            </Grid>
          )}
        </DownloadListItem>

        <DownloadListItem
          for={['single-component', 'custom']}
          description="Component IFC"
          visible={hasTrussDesignDownloadsPermission || !!sharedComponentSet}
          onClick={createBuildDownloadRequest(
            (downloadRequest) => dispatch(downloadConvertedFileType(downloadRequest, 'ifc', showAppSpinner)),
            'Component Design IFC file downloaded',
          )}
        />

        <DownloadListItem
          for={['whole-layout']}
          description="Layout IFC"
          visible={layoutPresent && (!!sharedComponentSet || hasNewLayoutModeFeatureFlag)}
          onClick={createDispatchDownloadWithUniqueRequest(
            selectedLevelGuid === 'all'
              ? exportActiveLayoutToIfcFile()
              : exportActiveLayoutToIfcFile(selectedLevelGuid, selectedLevelFullName),
            'Layout IFC file downloaded',
          )}
        >
          {(buildingSelect || layoutLevelSelect) && (
            <Grid container spacing={1}>
              {buildingSelect && <Grid size={layoutLevelSelect ? 6 : 12}>{buildingSelect}</Grid>}
              {layoutLevelSelect && <Grid size={buildingSelect ? 6 : 12}>{layoutLevelSelect}</Grid>}
            </Grid>
          )}
        </DownloadListItem>

        <DownloadListItem
          for={['whole-layout']}
          visible={layoutPresent}
          description="Layout PDF"
          onClick={async () => {
            await dispatch(
              selectedLevelGuid === 'all'
                ? downloadLayoutPdf()
                : downloadLayoutPdf(selectedLevelGuid, selectedLevelFullName, selectedLevelName),
            );
            recordInteraction('Layout PDF downloaded');
          }}
        >
          {(buildingSelect || layoutLevelSelect) && (
            <Grid container spacing={1}>
              {buildingSelect && <Grid size={layoutLevelSelect ? 6 : 12}>{buildingSelect}</Grid>}
              {layoutLevelSelect && <Grid size={buildingSelect ? 6 : 12}>{layoutLevelSelect}</Grid>}
            </Grid>
          )}
        </DownloadListItem>

        <DownloadListItem
          for={['single-component']}
          description="Component STL"
          visible={hasTrussDesignDownloadsPermission || !!sharedComponentSet}
          onClick={createNonAsyncDispatchDownloadWithUniqueRequest(
            downloadStlForActiveComponentDesign,
            'STL file downloaded',
          )}
        />

        <Divider />

        {/* section: material reports */}

        <DownloadListItem
          for={['single-component']}
          description="Load Case CSV"
          visible={hasTrussDesignDownloadsPermission && hasAnalysisEvaluationPermission}
          onClick={createDispatchDownloadWithUniqueRequest(downloadLoadCaseCsv, 'Load case CSV file downloaded')}
        />

        <DownloadListItem
          for={['any']}
          description="Lumber Pick List PDF"
          disabled={committedComponentDesigns.length === 0}
          onClick={createBuildDownloadRequest(
            (downloadRequest) => dispatch(downloadReport(downloadRequest, 'Lumber Pick List', showAppSpinner)),
            'Lumber Pick List file downloaded',
          )}
        />

        <DownloadListItem
          for={['any']}
          description="Plate Pick List PDF"
          onClick={createBuildDownloadRequest(
            (downloadRequest) => dispatch(downloadReport(downloadRequest, 'Plate Pick List', showAppSpinner)),
            'Plate Pick List file downloaded',
          )}
          disabled={platePickListPdfDownloadDisabled}
        />

        <DownloadListItem
          for={['any']}
          description="Material Details PDF"
          visible={hasTrussDesignDownloadsPermission || !!sharedComponentSet}
          disabled={committedComponentDesigns.length === 0}
          onClick={createBuildDownloadRequest(
            (downloadRequest) => dispatch(downloadReport(downloadRequest, 'Material Report PDF', showAppSpinner)),
            'Material Details PDF file downloaded',
          )}
        />

        <DownloadListItem
          for={['any']}
          description="Material Details CSV"
          visible={hasTrussDesignDownloadsPermission || !!sharedComponentSet}
          disabled={committedComponentDesigns.length === 0}
          onClick={createBuildDownloadRequest(
            (downloadRequest) => dispatch(downloadReport(downloadRequest, 'Material Report CSV', showAppSpinner)),
            'Material Details CSV file downloaded',
          )}
        />

        <DownloadListItem
          for={['any']}
          description="Component Report CSV"
          disabled={committedComponentDesigns.length === 0}
          onClick={createBuildDownloadRequest(
            (downloadRequest) => dispatch(downloadReport(downloadRequest, 'Component Report', showAppSpinner)),
            'Component Report CSV file downloaded',
          )}
        />

        {canComparePlatePrices && (
          <DownloadListItem
            for={['whole-project']}
            description="Plate Price Comparison CSV"
            visible={canComparePlatePrices}
            disabled={committedComponentDesigns.length === 0}
            onClick={() => dispatch(showSelectPlateListsDialog())}
          />
        )}

        <Divider />

        {/* section: drawing pdfs */}

        <DownloadListItem
          for={['single-component']}
          description="Shop Drawing PDF"
          disabled={!activeComponentDesign}
          onClick={onSeparateShopDrawingDownloadClick}
        />

        <DownloadListItem
          for={['whole-project', 'custom']}
          description="Shop Drawings PDF (separate cut sheets)"
          disabled={committedComponentDesigns.length === 0}
          onClick={onSeparateShopDrawingDownloadClick}
        />

        <DownloadListItem
          for={['whole-project', 'custom']}
          description="Shop Drawings PDF (combined cut sheets)"
          disabled={committedComponentDesigns.length === 0}
          onClick={createBuildDownloadRequest(
            (downloadRequest) => dispatch(downloadShopDrawings(downloadRequest, mode, true)),
            'Shop Drawings downloaded',
          )}
        />

        <TrussDesignDrawingDownloadListItem
          key={`tdd-${mode}`}
          for={['whole-project', 'single-component', 'custom']}
          description={`Truss Design Drawing${mode === 'single' ? '' : 's'} PDF`}
          visible={hasTrussDesignPermission || !!sharedComponentSet}
          disabled={committedComponentDesigns.length === 0}
          mode={mode}
          templates={trussDesignDrawingTemplates}
          canIgnoreErrors={canIgnoreTddErrors}
          canShareLayout={canShareProjectSnapshot}
          canChooseFileType={
            mode !== 'single' &&
            Object.values((mode === 'custom' ? customQuantities : downloadRequestForBulk?.guidToQuantity) ?? {}).filter(
              (quantity) => quantity !== 0,
            ).length > 1
          }
          onClick={(options) =>
            createBuildDownloadRequest(
              (downloadRequest) => dispatch(downloadTrussDesignDrawing(downloadRequest, mode, options)),
              'TDD file downloaded',
            )()
          }
        />

        {/* "Truss Design Drawing" for free users produce dark theme (non-printable) drawings */}
        <DownloadListItem
          for={['any']}
          upsell
          disabled
          description={`Truss Design Drawing${mode === 'single' ? '' : 's'} PDF (Printable)`}
          visible={!loggedInUser || isFreeUser}
          onClick={() => Promise.resolve()}
        />

        <Divider />

        {/* section: quality control */}

        <DownloadListItem
          for={['single-component']}
          description="Joint QC Detail PDF"
          onClick={() => Promise.resolve(void dispatch(showQualityControlPdfDownloadDialog()))}
          disabled={
            !activeAnalysisSet?.analogModel || activeAnalysisSet?.componentDesign.component.platePairs.length === 0
          }
          alternateTooltipExplainingDisabledState={
            !activeAnalysisSet?.analogModel
              ? 'Active component not analyzed'
              : activeAnalysisSet?.componentDesign.component.platePairs.length === 0
                ? 'Plates required'
                : undefined
          }
        />

        <DownloadListItem
          for={['single-component']}
          description="Digital QC File JSON"
          onClick={async () => {
            if (!activeComponentDesign) throw new Error('No active component design');
            const platePairs = activeComponentDesign.component.platePairs;
            const anyIntangiblePlates = platePairs.some((platePair) => {
              const plateManufacturer = getPlateManufacturer(platePair.plateType);
              return plateManufacturer === 'Any' || plateManufacturer === 'Combo';
            });
            anyIntangiblePlates
              ? dispatch(showQualityControlDigitalQcDownloadDialog())
              : await dispatch(generateDigitalQcFileForActiveComponentDesign({}));
          }}
          disabled={!activeAnalysisSet?.analogModel || !canUsePlates}
          alternateTooltipExplainingDisabledState={
            !activeAnalysisSet?.analogModel ? 'Active component not analyzed' : undefined
          }
        />

        <DownloadListItem
          for={['single-component']}
          description="Analysis Report PDF"
          visible={hasAnalysisEvaluationPermission}
          onClick={async () => {
            await dispatch(downloadAnalysisReportPdf());
            recordInteraction('Analysis report PDF file downloaded');
          }}
        />
        <Divider />

        {/* section: converted component files */}

        <DownloadListItem
          for={['any']}
          description={mode === 'bulk' ? 'BLADE files in ZIP' : 'Component BLADE'}
          visible={!sharedComponentSet && hasBladeFeatureFlag}
          onClick={createBuildDownloadRequest(
            (downloadRequest) => dispatch(downloadConvertedFileType(downloadRequest, 'blade', showAppSpinner)),
            'Blade file downloaded',
          )}
        />

        <DownloadListItem
          for={['any']}
          description={mode === 'bulk' ? 'BVN files in ZIP' : 'Component BVN'}
          onClick={createBuildDownloadRequest(
            (downloadRequest) => dispatch(downloadConvertedFileType(downloadRequest, 'bvn', showAppSpinner)),
            'BVN file downloaded',
          )}
        />

        <DownloadListItem
          for={['any']}
          description={
            mode === 'bulk' ? 'CYB file for all components' : isMemberSelected ? 'Selected Member CYB' : 'Component CYB'
          }
          visible={!sharedComponentSet && hasCybFeatureFlag}
          onClick={createBuildDownloadRequest(
            (downloadRequest) => dispatch(downloadConvertedFileType(downloadRequest, 'cyb', showAppSpinner)),
            'CYB file downloaded',
          )}
        />

        <DownloadListItem
          for={['single-component']}
          description="Component PCD"
          visible={isRootUser}
          onClick={createBuildDownloadRequest(
            (downloadRequest) =>
              // Special case: When viewing an old analysis set, download the PCD for that
              dispatch(
                downloadConvertedFileType(requestForAnalysisSetBeingViewed(downloadRequest), 'pcd', showAppSpinner),
              ),
            'PCD file downloaded',
          )}
        />

        <DownloadListItem
          for={['any']}
          description={mode === 'bulk' ? 'TPS file for all components' : 'Component TPS'}
          onClick={createBuildDownloadRequest(
            (downloadRequest) => dispatch(downloadConvertedFileType(downloadRequest, 'tps', showAppSpinner)),
            'TPS file downloaded',
          )}
          disabled={isFreeUser && !sharedComponentSet}
          upsell
        />

        <DownloadListItem
          for={['any']}
          description={mode === 'bulk' ? 'TRE files in ZIP' : 'Component TRE'}
          disabled={!loggedInUser || isFreeUser}
          upsell={isFreeUser && !sharedComponentSet}
          alternateTooltipExplainingDisabledState={
            sharedComponentSet && (!loggedInUser || isFreeUser)
              ? !loggedInUser
                ? 'Login or contact designer to access' // not logged in
                : trialAvailable
                  ? 'Start your free trial or contact designer to access' // free user, trial still available
                  : 'Upgrade or contact designer to access' // free user
              : undefined
          }
          onClick={createBuildDownloadRequest(
            (downloadRequest) => dispatch(downloadConvertedFileType(downloadRequest, 'tre', showAppSpinner)),
            'TRE file downloaded',
          )}
        />

        <DownloadListItem
          for={['any']}
          description={mode === 'bulk' ? 'Machinery TRS file for all components' : 'Machinery TRS'}
          onClick={createBuildDownloadRequest(
            (downloadRequest) =>
              dispatch(
                downloadMachineryTrs({
                  ...downloadRequest,
                  groupName:
                    trsMemberType === 'All'
                      ? downloadRequest.groupName
                      : `${downloadRequest.groupName} - ${trsMemberType}`,
                  memberType: trsMemberType,
                  maxMemberLengthInInches: useMachineryTrsMaxMemberLength ? machineryTrsMaxMemberLengthInInches : null,
                  includeMembersLongerThanMaxLengthInSeparateFile:
                    includeMembersLongerThanMaxLengthInSeparateMachineryTrsFile,
                }),
              ),
            'TRS Machinery file downloaded',
          )}
          disabled={isFreeUser && !sharedComponentSet}
          upsell
        >
          <Stack spacing={2} direction="column">
            <PropertySelectField<DownloadMachineryTrsMemberType>
              property="Select Member Type"
              options={['All', 'Chords', 'Webs', 'Blocks']}
              value={trsMemberType}
              submit={(value) => setTrsMemberType(value)}
            />
            <Stack spacing={2} direction="row" sx={{ whiteSpace: 'nowrap' }}>
              <FormControlLabel
                label="TRS only includes members of length or less"
                control={
                  <Checkbox
                    checked={useMachineryTrsMaxMemberLength}
                    onChange={(event) => setUseMachineryTrsMaxMemberLength(event.target.checked)}
                  />
                }
              />
              <DistanceField
                label="Max Length"
                value={machineryTrsMaxMemberLengthInInches}
                onSubmit={(value) => setMachineryTrsMaxMemberLengthInInches(value)}
                disabled={!useMachineryTrsMaxMemberLength}
              />
            </Stack>
            <FormControlLabel
              label="Include separate TRS file for members over max length"
              control={
                <Checkbox
                  checked={!!includeMembersLongerThanMaxLengthInSeparateMachineryTrsFile}
                  onChange={(event) =>
                    setIncludeMembersLongerThanMaxLengthInSeparateMachineryTrsFile(event.target.checked)
                  }
                  disabled={!useMachineryTrsMaxMemberLength}
                />
              }
            />
          </Stack>
        </DownloadListItem>

        <DownloadListItem
          for={['single-component']}
          description="Truss D&E"
          visible={!sharedComponentSet && hasSteelTrsFeatureFlag}
          onClick={createBuildDownloadRequest(
            (downloadRequest) => dispatch(downloadConvertedFileType(downloadRequest, 'steelTrs', showAppSpinner)),
            'Truss D&E file downloaded',
          )}
        />

        <DownloadListItem
          for={['whole-project']}
          description="Truss D&E"
          visible={!sharedComponentSet && hasSteelTrsFeatureFlag}
          disabled={committedComponentDesigns.length === 0}
          onClick={createBuildDownloadRequest(
            (downloadRequest) => dispatch(downloadConvertedFileType(downloadRequest, 'trussDAndE', showAppSpinner)),
            'Truss D&E package downloaded',
          )}
        />

        <DownloadListItem
          for={['any']}
          description={mode === 'bulk' ? 'OMN file for all components' : 'Component OMN'}
          onClick={createBuildDownloadRequest(
            (downloadRequest) => dispatch(downloadConvertedFileType(downloadRequest, 'omn', showAppSpinner)),
            'OMN file downloaded',
          )}
          disabled={isFreeUser && !sharedComponentSet}
          upsell
        />

        <Divider />

        {/* for internal usage */}
        <DownloadListItem
          for={['any']}
          description={isRecordingCanaries ? 'CSV - Recorded Canaries' : 'Record Canaries'}
          visible={isRootUser}
          onClick={async () => {
            if (isRecordingCanaries) {
              await dispatch(downloadCanaryCsv());
              dispatch(endCanaryRecording());

              recordInteraction('Canary CSV file downloaded');
            } else {
              dispatch(startCanaryRecordingIfHasPermissions());
            }
          }}
        />
      </Paper>
    </context.Provider>
  );
}
